import React from 'react';
import {ExhibitType, ExhibitTypeData} from "types/ExhibitType";
import {FormikHelpers} from "formik";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {useOperator} from "context/OperatorContext";
import Form, {
  ColourField,
  FormBlock,
  FormSection,
  MultiSelectField,
  SelectField,
  TextAreaField,
  TextField
} from 'components/Form/Form';
import {Option} from "components/Form/Form/Control";
import IconName from "../../../types/IconName";
import {toTitleCase} from "../../../services/string";
import * as Yup from "yup";


// Types ----

type ExhibitTypeFormProps = {
  exhibitType?: ExhibitType | null
  onSubmit: (values: ExhibitTypeData, helpers: FormikHelpers<ExhibitTypeData>) => any
}

// Helpers ----

const iconOptions: Array<Option> = Object.values(IconName).map(icon => ({
  id: icon, name: toTitleCase(icon), icon: icon
}))

async function getFactTemplateOptions(): Promise<Array<Option>> {
  const factTemplates = await ngage.factTemplates.all(1).then(page => page?.data ?? [])

  return factTemplates.map(template => ({
    type: 'fact-template',
    id: template.id,
    name: template.name,
    icon: template.icon,
    description: template.type
  }))
}

// Component ----

function ExhibitTypeForm({exhibitType, onSubmit}: ExhibitTypeFormProps) {
  const {termFor} = useOperator()

  const {data: factTemplateOptions, isLoading: factTemplateOptionsLoading} = useQuery(
    ['fact-templates.options', {page: 1}],
    () => getFactTemplateOptions()
  )

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("This field is required").max(250, "Maximum character limit exceeded"),
    icon: Yup.string().required("This field is required"),
    description: Yup.string().max(250, "Maximum character limit exceeded"),
  })

  const initialValues: ExhibitTypeData = {
    name: exhibitType?.name ?? '',
    description: exhibitType?.description ?? '',
    colour: exhibitType?.colour ?? '#2b2d30',
    icon: exhibitType?.icon ?? IconName.INFORMATION_CIRCLE,
    fact_template_ids: exhibitType?.fact_templates?.map(template => template.id.toString()) ?? []
  }

  return (
    <Form initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      <FormSection
        name='Key Information'
        description={`Basic details about the ${termFor('exhibit type')}`}
        columns={6}
      >

        {/* Name */}
        <FormBlock columns={3}>
          <TextField
            name="name"
            label="Name *"
            hint="250 chars max"
            help="The publicly visible display name"
            autoComplete="off"
            maxLength={250}
            autoFocus
          />
        </FormBlock>

        {/* Icon */}
        <FormBlock columns={2}>
          <SelectField
            name="icon"
            label="Icon *"
            help="The icon shown with the name"
            options={iconOptions}
          />
        </FormBlock>

        {/* Description */}
        <FormBlock columns={6}>
          <TextAreaField
            name="description"
            label="Description"
            hint="Optional. 250 chars max."
            help="Brief descriptive information to clarify how this should be used"
            autoComplete="off"
            maxChars={250}
            rows={5}
          />
        </FormBlock>

        {/* Colour */}
        <FormBlock columns={1}>
          <ColourField
            name="colour"
            label="Colour *"
            autoComplete="off"
          />
        </FormBlock>
      </FormSection>

      <FormSection
        name="Facts"
        description={`Choose the ${termFor('facts')} that ${termFor('exhibits')} of this ${termFor('exhibit type')} always have`}
        columns={6}
      >
        {/* Fact Templates */}
        <FormBlock columns={6}>
          <MultiSelectField
            name="fact_template_ids"
            label={`Select ${termFor('Facts')}`}
            hint="Optional"
            options={factTemplateOptions ?? []}
            isLoading={factTemplateOptionsLoading}
          />
        </FormBlock>
      </FormSection>
    </Form>
  );
}

export default ExhibitTypeForm;